import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatDatepicker, MatDatepickerInput, MatDatepickerToggle } from '@angular/material/datepicker';
import { MatError, MatFormField, MatInput, MatLabel, MatSuffix } from '@angular/material/input';
import { MatOption, MatSelect } from '@angular/material/select';
import { ArraySortPipe, ControlErrorDirective, HowdenTableComponent, IPageOf, ITableEventData, PageTitleComponent, UIBlockerService } from '@howdeniberia/core-front';
import { IMapRating } from 'src/app/core/models/api';
import { ICategoryService } from 'src/app/core/services/api/category';
import { IMapRatingService } from 'src/app/core/services/api/map';
import { FileUtils, Limits } from 'src/app/shared/utils';
import { SubSink } from 'subsink';
import { RiskRatingMapper } from '../../mapper';
import { RiskRatingSearchViewModel } from '../../models';

@Component({
  selector: 'howden-risk-rating-search',
  templateUrl: './risk-rating-search.component.html',
  styleUrl: './risk-rating-search.component.scss',
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatInput,
    MatLabel,
    MatFormField,
    MatError,
    MatSelect,
    MatOption,
    MatButton,
    MatDatepickerInput,
    MatDatepickerToggle,
    MatSuffix,
    MatDatepicker,
    ArraySortPipe,
    ControlErrorDirective,
    PageTitleComponent,
    HowdenTableComponent
  ]
})
export class RiskRatingSearchComponent implements OnInit, OnDestroy {
  private categorySrv = inject(ICategoryService);
  private mapRatingSrv = inject(IMapRatingService);
  private uiBlockerSrv = inject(UIBlockerService);

  model = new RiskRatingSearchViewModel();

  get minDate(): Date { return Limits.minDate; }

  private _subscriptions = new SubSink();

  ngOnInit(): void {
    this.categorySrv.getCategories().subscribe((data) => {
      this.model.categories = data ?? [];
      this.refresh();
      this.onChanges();
    });
  }

  ngOnDestroy(): void {
    this._subscriptions.unsubscribe();
  }

  onServerSideConfigChanged(event: ITableEventData): void {
    this.model.updateServerSideConfig(event);
    this.refresh();
  }

  onDownloadExcel(): void {
    this.uiBlockerSrv.block();

    this.model.updateServerSideFilters();

    this.mapRatingSrv.downloadExcel(this.model.searchRequest).subscribe(
      {
        next: (blob: Blob) => {
          const attachName = FileUtils.formatFilename('RiskRating', 'xlsx');
          FileUtils.downloadFile(
            attachName,
            blob
          );
        },
        complete: () => {
          this.uiBlockerSrv.unblock();
        }
      }
    );
  }

  private onChanges(): void {
    this._subscriptions.sink = this.model.filterForm.valueChanges.subscribe(() => {
      this.model.searchRequest.pageNumber = 0;
      this.model.updateServerSideFilters();
      this.refresh();
    });
  }

  private refresh(): void {
    this.model.updateServerSideFilters();

    this.uiBlockerSrv.block();

    this.mapRatingSrv.search(this.model.searchRequest).subscribe({
      next: (searchResult: IPageOf<IMapRating>) => {
        this.model.length = searchResult.totalCount;
        this.model.data = searchResult.data?.map((source) =>
          RiskRatingMapper.mapForSearch(source)
        );
      },
      complete: () => {
        this.uiBlockerSrv.unblock();
      }
    });
  }
}
